import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isAdmin as funcIsAdmin, isClientPreStatus, isProfessionalPreStatus, isCanUseOnlyProffitSearch as funcIsCanUseOnlyProffitSearch, isCanUseProffitSearch as funcIsCanUseProffitSearch, isCanUseClientProffit, isCanUseProfessionalProffitMart, isCanUseProfessionalProffit, isCanUseProfessionalSeminar as funcIsCanUseProfessionalSeminar, } from '@/helpers/auth';
import UserPreviewState from '@/helpers/user/UserPreviewState';
import { hasRequiredParam } from '@/helpers/user/UserProfileHelper';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { isLogin as funcIsLogin, isGuest as funcIsGuest, isClient as funcIsClient, isProfessional as funcIsProfessional, isFreeProfessional as funcIsFreeProfessional, } from '@/utils/resources/auth/authResource';
import { loginPath, myPagePath, profilePath, searchLpPath, } from '@/utils/router/links';
/**
 *  ユーザーの権限やログイン状態に応じて、リダイレクトかどうか判定
 *  TODO: 今後はチェック項目ごとに hooks を分けて管理したい
 * @param args
 */
export const useAuthentication = (args) => {
    const auth = useTypeSelector((state) => state.auth);
    const profile = useTypeSelector((state) => state.profile);
    const { pathname } = useLocation();
    const { isAdmin = null, isCanUseProfessionalSeminar = null, isCanUseProffit = null, isCanUseProffitMart = null, isCanUseProffitSearch = null, isClient = null, isForceAdmin = null, isFreeProfessional = null, isGuest = null, isMember = null, isNeed = null, isNotRequireCheckProfile = null, isPreClient = null, isPreProfessional = null, isPreview = null, isProfessional = null, notClientRedirectTo = loginPath(), notFreeProfessionalRedirectTo = loginPath(), notGuestRedirectTo = myPagePath(), notIsAdminRedirectTo = myPagePath(), notIsCanUseProfessionalSeminarRedirectTo = myPagePath(), notIsCanUseProffitMartRedirectTo = myPagePath(), notIsCanUseProffitRedirectTo = myPagePath(), notIsCanUseProffitSearchRedirectTo = searchLpPath(), notIsNeedRedirectTo = loginPath(), notMemberRedirectTo = loginPath(), notPreClientRedirectTo = loginPath(), notPreProfessionalRedirectTo = myPagePath(), notProfessionalRedirectTo = loginPath(), } = args;
    const redirectSetting = useMemo(() => {
        // 必要な情報を所持しているかどうか判定
        // 利用可能な権限がSERVICE_PROFFIT_SEARCHだけの場合はプロフィールは必須ではないのでスキップ
        const isLoginState = funcIsLogin(auth);
        const isProfessionalState = funcIsProfessional(auth);
        const isClientState = funcIsClient(auth);
        const isClientPreState = isClientPreStatus(profile);
        const isProfessionalPreStatusState = isProfessionalPreStatus(profile);
        const isFreeProfessionalState = funcIsFreeProfessional(auth);
        const isCanUseOnlyProffitSearch = funcIsCanUseOnlyProffitSearch(profile);
        const isCanUseProffitSearchState = funcIsCanUseProffitSearch(profile);
        const isCanUseProfessionalSeminarState = isProfessionalState && funcIsCanUseProfessionalSeminar(profile);
        const isCanUseProffitMartState = (() => {
            if (isClientState) {
                return isCanUseClientProffit(profile);
            }
            if (isProfessionalState) {
                return isCanUseProfessionalProffitMart(profile);
            }
            return false;
        })();
        const isCanUseProffitState = (() => {
            if (isClientState) {
                return isCanUseClientProffit(profile);
            }
            if (isProfessionalState || isFreeProfessionalState) {
                return isCanUseProfessionalProffit(profile);
            }
            return false;
        })();
        const isGuestState = funcIsGuest(auth);
        const isAdminState = funcIsAdmin(profile);
        const hasRequiredParamState = hasRequiredParam(auth, profile);
        // プレビュー判定
        if (isPreview && UserPreviewState.isPreview) {
            return null;
        }
        // 管理者であれば強制的に表示可能
        if (isForceAdmin && isAdminState) {
            return null;
        }
        // プロフィール必須入力判定チェック
        if (isLoginState &&
            !isCanUseOnlyProffitSearch &&
            !hasRequiredParamState &&
            !isNotRequireCheckProfile &&
            pathname !== profilePath()) {
            return { redirect: profilePath() };
        }
        // コンサルの仮登録判定
        if (isPreProfessional !== null &&
            isProfessionalState &&
            isPreProfessional !== isProfessionalPreStatusState) {
            return { redirect: notPreProfessionalRedirectTo };
        }
        // コンサル判定
        if (isProfessional !== null && isProfessional !== isProfessionalState) {
            return { redirect: notProfessionalRedirectTo };
        }
        // フリーコンサル判定
        if (isFreeProfessional !== null &&
            isFreeProfessional !== isFreeProfessionalState) {
            return { redirect: notFreeProfessionalRedirectTo };
        }
        // 企業ユーザの仮登録判定
        if (isPreClient !== null &&
            isClientState &&
            isPreClient !== isClientPreState) {
            return { redirect: notPreClientRedirectTo };
        }
        // 企業ユーザ判定
        if (isClient !== null && isClient !== isClientState) {
            return { redirect: notClientRedirectTo };
        }
        // 会員判定
        if (isMember !== null && isMember !== isLoginState) {
            return { redirect: notMemberRedirectTo };
        }
        // ゲスト判定
        if (isGuest !== null && isGuest !== isGuestState) {
            return { redirect: notGuestRedirectTo };
        }
        // 管理者判定
        if (isAdmin !== null && isAdmin !== isAdminState) {
            return { redirect: notIsAdminRedirectTo };
        }
        // その他設定
        if (isNeed !== null && !isNeed) {
            return { redirect: notIsNeedRedirectTo };
        }
        // ProffitSearch利用可否判定
        if (isCanUseProffitSearch !== null &&
            isCanUseProffitSearch !== isCanUseProffitSearchState) {
            const { proffitSearch } = profile || {};
            const { isAccountDisable } = proffitSearch || {};
            return {
                message: isAccountDisable
                    ? 'ProffitSearchの使用が管理者に無効化されています'
                    : '',
                redirect: notIsCanUseProffitSearchRedirectTo,
            };
        }
        // Proffit利用可否判定
        if (isCanUseProffit !== null && isCanUseProffit !== isCanUseProffitState) {
            return { redirect: notIsCanUseProffitRedirectTo };
        }
        // ProffitMart利用可否判定
        if (isCanUseProffitMart !== null &&
            isCanUseProffitMart !== isCanUseProffitMartState) {
            return { redirect: notIsCanUseProffitMartRedirectTo };
        }
        // セミナー利用可否判定
        if (isCanUseProfessionalSeminar !== null &&
            isCanUseProfessionalSeminar !== isCanUseProfessionalSeminarState) {
            return { redirect: notIsCanUseProfessionalSeminarRedirectTo };
        }
        return null;
    }, [
        auth,
        profile,
        pathname,
        isPreview,
        isNotRequireCheckProfile,
        isPreProfessional,
        isProfessional,
        isFreeProfessional,
        isPreClient,
        isClient,
        isMember,
        isGuest,
        isAdmin,
        isForceAdmin,
        isNeed,
        isCanUseProffitSearch,
        isCanUseProffit,
        isCanUseProffitMart,
        isCanUseProfessionalSeminar,
        notPreProfessionalRedirectTo,
        notProfessionalRedirectTo,
        notFreeProfessionalRedirectTo,
        notPreClientRedirectTo,
        notClientRedirectTo,
        notMemberRedirectTo,
        notGuestRedirectTo,
        notIsAdminRedirectTo,
        notIsNeedRedirectTo,
        notIsCanUseProffitSearchRedirectTo,
        notIsCanUseProffitRedirectTo,
        notIsCanUseProffitMartRedirectTo,
        notIsCanUseProfessionalSeminarRedirectTo,
    ]);
    return {
        message: (redirectSetting === null || redirectSetting === void 0 ? void 0 : redirectSetting.message) || '',
        profile,
        redirectTo: (redirectSetting === null || redirectSetting === void 0 ? void 0 : redirectSetting.redirect) || '',
    };
};
